import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { format } from 'date-fns'

import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './awayMessage.module.scss'
import { OOOStates } from './OutOfOfficeContainer'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { useClientsData } from '../../lyraTherapy/clients/data/hooks/useClientsData'
import { useSendOOOMessageMutation } from '../../lyraTherapy/messages/data/messagesApi'

export const ConfirmationPrompt: React.FC<ConfirmationPromptProps> = ({
  isScheduled,
  message,
  onContinue,
  scheduledDate,
  scheduledTime,
}) => {
  const { data: clientsActive } = useClientsData({ status: 'active' })
  const userId = useSelector(getAuthUserId)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createOOOMessage] = useSendOOOMessageMutation()

  return (
    <div>
      <h2>{`You are sending this message to ${clientsActive?.length} active clients.`}</h2>
      <p className={styles['prompt-text']}>
        You won&apos;t be able to edit or retract this message once scheduled or sent. Make sure your message does not
        contain any PHI and is appropriate for all active clients.
      </p>

      <div className={styles.buttons}>
        <SecondaryButton
          fullWidth={true}
          text={'Send'}
          onPress={() => {
            if (!userId) {
              return
            }
            setIsSubmitting(true)
            createOOOMessage({
              message_body: message,
              message_type: 'out_of_office',
              provider_lyra_id: userId,
              time_zone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
              ...(isScheduled && {
                delivery_datetime: format(new Date(`${scheduledDate} ${scheduledTime}`), 'yyyy-MM-dd HH:mm:ss'),
              }),
            })
              .unwrap()
              .then(() => {
                setIsSubmitting(false)
                track({ event: 'SEND_OOO_MESSAGE_SELECTED' })
                onContinue(OOOStates.DISPLAYING_CONFIRMATION)
              })
          }}
          loading={isSubmitting}
          testID={tID('ConfirmationPrompt-confirm')}
        />
        <PrimaryButton
          fullWidth={true}
          text={'Keep editing'}
          onPress={() => {
            track({ event: 'GO_BACK_TO_EDIT_OOO_MESSAGE_SELECTED' })
            onContinue(OOOStates.SETTING_MESSAGE)
          }}
          testID={tID('ConfirmationPrompt-go-back')}
          disabled={isSubmitting}
        />
      </div>
    </div>
  )
}

type ConfirmationPromptProps = {
  message: string
  isScheduled: boolean
  scheduledDate: string | null
  scheduledTime: string | null
  onContinue: (newOperation: string) => void
}

export default ConfirmationPrompt
